import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChecklist } from '@core/hooks/useChecklist';
import { Button } from '@shared/components/Button';
import { Input } from '@shared/components/Input';
import { Textarea } from '@shared/components/Textarea';
import { Question } from '@core/types';

const ChecklistCreatePage: React.FC = () => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [questions, setQuestions] = useState<Omit<Question, 'id'>[]>([
    { text: '', type: 'TEXT', order: 0, required: false },
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { createChecklist } = useChecklist();
  const navigate = useNavigate();

  const addQuestion = () => {
    setQuestions([
      ...questions,
      { text: '', type: 'TEXT', order: questions.length, required: false },
    ]);
  };

  const removeQuestion = (index: number) => {
    if (questions.length === 1) return;
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const updateQuestion = (index: number, field: keyof Omit<Question, 'id'>, value: any) => {
    const updated = [...questions];
    updated[index] = { ...updated[index], [field]: value };
    setQuestions(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    if (questions.some((q) => !q.text.trim())) {
      alert('Todas las preguntas deben tener texto');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim() || undefined,
        questions: questions.map((q, i) => ({
          ...q,
          order: i,
          options: q.type === 'CHOICE' ? q.options || [] : undefined,
        })),
      };
      await createChecklist(payload);
      navigate('/checklists');
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const questionTypeOptions = [
    { value: 'TEXT', label: 'Texto' },
    { value: 'NUMBER', label: 'Número' },
    { value: 'BOOLEAN', label: 'Sí/No' },
    { value: 'CHOICE', label: 'Opción múltiple' },
    { value: 'DATE', label: 'Fecha' },
    { value: 'PHOTO', label: 'Foto' },
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl mx-auto">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Crear Checklist</h1>
        <div className="space-x-2">
          <Button variant="secondary" onClick={() => navigate('/checklists')}>
            Cancelar
          </Button>
          <Button type="submit" isLoading={isSubmitting}>
            Guardar
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <Input
          label="Título"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          placeholder="Ej: Checklist de Seguridad"
        />
        <Textarea
          label="Descripción"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Descripción opcional"
          rows={2}
        />
      </div>

      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900">Preguntas</h2>
          <Button variant="secondary" size="sm" onClick={addQuestion}>
            + Agregar pregunta
          </Button>
        </div>

        {questions.map((question, index) => (
          <div key={index} className="border border-gray-200 rounded-lg p-4 space-y-3">
            <div className="flex justify-between items-start">
              <span className="text-sm font-medium text-gray-500">Pregunta {index + 1}</span>
              <Button
                variant="danger"
                size="sm"
                onClick={() => removeQuestion(index)}
                disabled={questions.length === 1}
              >
                Eliminar
              </Button>
            </div>

            <Input
              value={question.text}
              onChange={(e) => updateQuestion(index, 'text', e.target.value)}
              placeholder="Texto de la pregunta"
              required
            />

            <div className="grid grid-cols-2 gap-3">
              <select
                value={question.type}
                onChange={(e) => updateQuestion(index, 'type', e.target.value)}
                className="rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              >
                {questionTypeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>

              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={question.required}
                  onChange={(e) => updateQuestion(index, 'required', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Obligatoria</span>
              </label>
            </div>

            {question.type === 'CHOICE' && (
              <div className="space-y-2">
                <Input
                  value={question.options?.join(', ') || ''}
                  onChange={(e) =>
                    updateQuestion(
                      index,
                      'options',
                      e.target.value.split(',').map((s) => s.trim()).filter(Boolean)
                    )
                  }
                  placeholder="Opciones separadas por coma: Ej: Sí, No, N/A"
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </form>
  );
};

export default ChecklistCreatePage;